import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { Link } from 'react-router-dom';

/**
 * Replaces ViewData["Breadcrumbs"]. A page calls useBreadcrumbs([...]) and the topbar picks
 * them up; the list is cleared automatically when the page unmounts.
 */
export interface Crumb {
  label: string;
  to?: string;
}

interface BreadcrumbContextValue {
  crumbs: Crumb[];
  setCrumbs: (crumbs: Crumb[]) => void;
}

const BreadcrumbContext = createContext<BreadcrumbContextValue | undefined>(undefined);

export function BreadcrumbProvider({ children }: { children: ReactNode }) {
  const [crumbs, setCrumbs] = useState<Crumb[]>([]);
  const value = useMemo(() => ({ crumbs, setCrumbs }), [crumbs]);
  return <BreadcrumbContext.Provider value={value}>{children}</BreadcrumbContext.Provider>;
}

/** Sets the topbar breadcrumbs for as long as the calling component is mounted. */
export function useBreadcrumbs(crumbs: Crumb[]) {
  const ctx = useContext(BreadcrumbContext);
  // Serialised so an inline array literal does not re-fire the effect on every render.
  const key = JSON.stringify(crumbs);

  useEffect(() => {
    ctx?.setCrumbs(JSON.parse(key) as Crumb[]);
    return () => ctx?.setCrumbs([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);
}

export function BreadcrumbTrail() {
  const ctx = useContext(BreadcrumbContext);
  const crumbs = ctx?.crumbs ?? [];
  if (crumbs.length === 0) return null;

  return (
    <nav aria-label="breadcrumb" className="d-none d-md-block">
      <ol className="breadcrumb mb-0">
        {crumbs.map((crumb, index) => {
          const isLast = index === crumbs.length - 1;
          if (isLast) {
            return (
              <li key={`${crumb.label}-${index}`} className="breadcrumb-item active">
                {crumb.label}
              </li>
            );
          }
          return (
            <li key={`${crumb.label}-${index}`} className="breadcrumb-item">
              {crumb.to ? <Link to={crumb.to}>{crumb.label}</Link> : crumb.label}
            </li>
          );
        })}
      </ol>
    </nav>
  );
}
